import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from 'components/Modal/Modal';
import { VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES } from 'constants/general';
import { BUTTON_VARIANT, EVCAnalysisResultStatus } from 'constants/interfaces';
import dayjs from 'dayjs';
import formatCompactCurrency from 'utils/formatCompactCurrency';
import getPercentage from 'utils/getPercentage';

import AnalysisResultCardFooter from './components/AnalysisResultCardFooter/AnalysisResultCardFooter';
import AnalysisResultCardHeader from './components/AnalysisResultCardHeader/AnalysisResultCardHeader';
import ImpactedCustomersSection from './components/ImpactedCustomersSection/ImpactedCustomersSection';
import ImpactedProductsSection from './components/ImpactedProductsSection/ImpactedProductsSection';
import ProfitCentresSection from './components/ProfitCentresSection/ProfitCentresSection';
import RevenueImpactSection from './components/RevenueImpactSection/RevenueImpactSection';
import ScenarioItem from './components/ScenarioItem/ScenarioItem';

import type IAnalysisResultCard from './interfaces/IAnalysisResultCard';

import styles from './sass/AnalysisResultCard.module.scss';

const RESULT_CARD_DATE_FORMAT = 'D MMM., hh:mm A';
type TConfirmationAction = 'delete' | 'duplicate';

function AnalysisResultCard({
  result,
  onViewDetails,
  onOpenDetails,
  onToggleVisibility,
  onDuplicate,
  onDelete,
  isDuplicateLoading = false,
  isDeleteLoading = false,
  isVisibilityActive = false,
  showHeaderActions = true,
}: IAnalysisResultCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<TConfirmationAction | null>(null);

  const {
    impactedProductsCount,
    totalProductsCount,
    impactedProfitCentresCount,
    totalProfitCentresCount,
    impactedCustomersCount,
    totalCustomersCount,
    customerRevenueImpact,
    customerRevenueImpactPercentage,
    createdAt,
    totalEntities,
    totalScenarios,
  } = result;

  const isProcessing = VALUE_CHAIN_MAP_ANALYSIS_RESULT_PROCESSING_STATUSES.has(result.status);

  const formattedDate = createdAt ? dayjs(createdAt).format(RESULT_CARD_DATE_FORMAT) : '—';
  const formattedRevenue = formatCompactCurrency(customerRevenueImpact);

  const productsPercentage = getPercentage(impactedProductsCount, totalProductsCount);
  const profitCentresPercentage = getPercentage(
    impactedProfitCentresCount,
    totalProfitCentresCount,
  );
  const customersPercentage = getPercentage(impactedCustomersCount, totalCustomersCount);

  const handleToggleDetails = () => {
    if (onOpenDetails) {
      onOpenDetails();
      return;
    }

    const willOpen = !isDetailsOpen;
    setIsDetailsOpen(willOpen);
    if (willOpen) onViewDetails?.();
  };

  const handleOpenDeleteConfirmation = () => {
    setConfirmationAction('delete');
  };

  const handleOpenDuplicateConfirmation = () => {
    setConfirmationAction('duplicate');
  };

  const handleCloseConfirmation = () => {
    setConfirmationAction(null);
  };

  const handleConfirmAction = async () => {
    const actionHandler = confirmationAction === 'delete' ? onDelete : onDuplicate;

    if (!actionHandler) {
      handleCloseConfirmation();
      return;
    }

    try {
      await actionHandler();
      handleCloseConfirmation();
    } catch {
      toast.error(t('common.unknownError'));
    }
  };

  const isDeleteConfirmation = confirmationAction === 'delete';
  const isConfirmationOpen = confirmationAction !== null;
  const isConfirmationLoading = isDeleteConfirmation ? isDeleteLoading : isDuplicateLoading;
  const confirmationTitle = isDeleteConfirmation
    ? t('valueChainMap.resultCardDeleteTitle')
    : t('valueChainMap.resultCardDuplicateTitle');
  const confirmationDescription = isDeleteConfirmation
    ? t('valueChainMap.resultCardDeleteDescription')
    : undefined;
  const confirmationConfirmTitle = isDeleteConfirmation
    ? t('valueChainMap.resultCardDeleteConfirm')
    : t('valueChainMap.resultCardDuplicateConfirm');
  const confirmationCloseTitle = isDeleteConfirmation
    ? t('valueChainMap.resultCardDeleteCancel')
    : t('valueChainMap.resultCardDuplicateCancel');

  if (isProcessing) {
    return (
      <div
        className={styles.card}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <AnalysisResultCardHeader
          formattedDate={formattedDate}
          onDelete={handleOpenDeleteConfirmation}
          onDuplicate={handleOpenDuplicateConfirmation}
        />

        <div className={styles.processingBody}>
          <div className={styles.loader} aria-hidden="true">
            <span className={styles.loaderTrack} />
            <span className={styles.loaderArc} />
          </div>
          <p className={styles.processingTitle}>
            {t('valueChainMap.resultCardProcessingTitle')}
          </p>
          <p className={styles.processingDescription}>
            {t('valueChainMap.resultCardProcessingDescription')}
          </p>
        </div>
      </div>
    );
  }

  if (result.status === EVCAnalysisResultStatus.ERROR) {
    return (
      <div
        className={styles.card}
        role="alert"
        aria-live="assertive"
      >
        <AnalysisResultCardHeader
          formattedDate={formattedDate}
          onDelete={handleOpenDeleteConfirmation}
          onDuplicate={handleOpenDuplicateConfirmation}
        />

        <div className={styles.failedBody}>
          <p className={styles.failedTitle}>
            {t('valueChainMap.resultCardFailedTitle')}
          </p>
          <p className={styles.failedDescription}>
            {t('valueChainMap.resultCardFailedDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <AnalysisResultCardHeader
        formattedDate={formattedDate}
        onDelete={handleOpenDeleteConfirmation}
        onDuplicate={handleOpenDuplicateConfirmation}
        onToggleVisibility={onToggleVisibility}
        showVisibilityButton={showHeaderActions}
        isVisibilityActive={isVisibilityActive}
      />

      <div className={styles.grid}>
        <ImpactedProductsSection
          impactedProductsCount={impactedProductsCount}
          productsPercentage={productsPercentage}
          totalProductsCount={totalProductsCount}
        />
        <ProfitCentresSection
          impactedProfitCentresCount={impactedProfitCentresCount}
          profitCentresPercentage={profitCentresPercentage}
          totalProfitCentresCount={totalProfitCentresCount}
        />
        <ImpactedCustomersSection
          customersPercentage={customersPercentage}
          impactedCustomersCount={impactedCustomersCount}
          totalCustomersCount={totalCustomersCount}
        />
        <RevenueImpactSection
          customerRevenueImpactPercentage={customerRevenueImpactPercentage}
          formattedRevenue={formattedRevenue}
        />
      </div>

      <AnalysisResultCardFooter
        isDetailsOpen={isDetailsOpen}
        onToggleDetails={handleToggleDetails}
        totalEntities={totalEntities}
        totalScenarios={totalScenarios}
      />

      {isDetailsOpen && result.scenarios.length > 0 ? (
        <div className={styles.detailsPanel}>
          <span className={styles.detailsScenariosLabel}>
            {t('valueChainMap.resultCardScenariosLabel')}
            :
          </span>
          {result.scenarios.map((scenario) => (
            <ScenarioItem
              key={scenario.id}
              scenario={scenario}
            />
          ))}
        </div>
      ) : null}

      <Modal
        open={isConfirmationOpen}
        title={confirmationTitle}
        description={confirmationDescription}
        closeTitle={confirmationCloseTitle}
        confirmTitle={confirmationConfirmTitle}
        confirmVariant={isDeleteConfirmation ? BUTTON_VARIANT.error : BUTTON_VARIANT.primary}
        onReject={handleCloseConfirmation}
        onConfirm={handleConfirmAction}
        onClose={handleCloseConfirmation}
        isLoading={isConfirmationLoading}
      />
    </div>
  );
}

export default AnalysisResultCard;
